import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { DatabaseLog, Label, Client, PaginatedLogsResponse } from '../models/database-log.model';
import { Version } from '../models/version.model';
import { Client as ClientManagement, ClientWorkstation, WorkstationRoleType, LocalLabel, LabelUsage, WorkstationRoleTypeRequest, RoleArtifactStatus } from '../models/client-management.model';
import { DatabaseMapping, MappingVersionInfo, UpgradeResult } from '../models/database-mapping.model';
import { VersionLog, S3ExportResult, S3Status } from '../models/aws-export.model';
import {
  CreatePresetRequest,
  PresetInfo,
  PresetJsonStructure,
  PresetOperationResult,
  DatabaseTableInfo
} from '../models/configuration-preset.model';
import {
  ClientUser,
  CreateClientUserRequest,
  UpdateClientUserRequest,
  UserLoginRequest,
  UserLoginResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  AuthenticatedUser,
  LoginAttempt,
  LoginAttemptsResponse
} from '../models/client-user.model';
import {
  RagAskRequest,
  RagAskResponse,
  GcsFileItem,
  GcsUploadResponse,
  ConversationHistoryItem,
  UsageLimitInfo
} from '../models/rag.model';
import {
  ReportType,
  Report,
  CreateReportRequest,
  ReportListResponse
} from '../models/report.model';
import { AgentTasksResponse } from '../models/agent-task.model';
import { SwitchPortDto, PortActionResult } from '../models/switch-port.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private get apiUrl() { return `${this.config.apiUrl}/api`; }

  constructor(private http: HttpClient, private config: AppConfigService) { }

  // Auth endpoints
  getCurrentUser(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/auth/user`, { withCredentials: true });
  }

  // Database endpoints
  getDatabases(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/database/list`, { withCredentials: true });
  }

  getTrackedDatabases(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/database/list/tracked`, { withCredentials: true });
  }

  getDatabaseStatus(database: string): Observable<{ isTracked: boolean; hasLogs: boolean; hasVersions: boolean; hasTrigger: boolean }> {
    return this.http.get<{ isTracked: boolean; hasLogs: boolean; hasVersions: boolean; hasTrigger: boolean }>(
      `${this.apiUrl}/database/${database}/status`, { withCredentials: true });
  }

  initializeDatabase(database: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/database/${database}/initialize`, {}, { withCredentials: true });
  }

  setupVersions(database: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/database/${database}/setup-versions`, {}, { withCredentials: true });
  }

  getDatabaseTables(database: string): Observable<DatabaseTableInfo[]> {
    return this.http.get<DatabaseTableInfo[]>(`${this.apiUrl}/database/${database}/tables`, { withCredentials: true });
  }

  // Version endpoints
  getVersions(database: string): Observable<Version[]> {
    return this.http.get<Version[]>(`${this.apiUrl}/versions/${database}`, { withCredentials: true });
  }

  getLatestVersion(database: string): Observable<Version> {
    return this.http.get<Version>(`${this.apiUrl}/versions/${database}/latest`, { withCredentials: true });
  }

  createVersion(database: string, versionName: string, dbLogID?: number, affectedRoleTypeIds?: number[]): Observable<Version> {
    return this.http.post<Version>(`${this.apiUrl}/versions/${database}`,
      { versionName, dbLogID, affectedRoleTypeIds },
      { withCredentials: true });
  }

  // DatabaseLog endpoints
  getLogsFromVersion(
    database: string,
    versionId: number,
    objectType?: string,
    userName?: string,
    page: number = 1,
    pageSize: number = 50,
    dateFrom?: string,
    dateTo?: string,
    objectName?: string,
    labelIds?: number[],
    labelMatchMode: string = 'any'
  ): Observable<PaginatedLogsResponse> {
    let url = `${this.apiUrl}/databaselogs/${database}/from-version/${versionId}`;
    const params: string[] = [];

    params.push(`page=${page}`);
    params.push(`pageSize=${pageSize}`);

    if (objectType) {
      params.push(`objectType=${encodeURIComponent(objectType)}`);
    }
    if (userName) {
      params.push(`userName=${encodeURIComponent(userName)}`);
    }
    if (dateFrom) {
      params.push(`dateFrom=${encodeURIComponent(dateFrom)}`);
    }
    if (dateTo) {
      params.push(`dateTo=${encodeURIComponent(dateTo)}`);
    }
    if (objectName) {
      params.push(`objectName=${encodeURIComponent(objectName)}`);
    }
    if (labelIds && labelIds.length > 0) {
      params.push(`labelIds=${labelIds.join(',')}`);
      params.push(`labelMatchMode=${labelMatchMode}`);
    }

    url += '?' + params.join('&');

    return this.http.get<PaginatedLogsResponse>(url, { withCredentials: true });
  }

  createLog(database: string, log: Partial<DatabaseLog>): Observable<DatabaseLog> {
    return this.http.post<DatabaseLog>(`${this.apiUrl}/databaselogs/${database}`, log, { withCredentials: true });
  }

  markAsDeleted(database: string, id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/databaselogs/${database}/${id}`, { withCredentials: true });
  }

  exportVersion(database: string, versionId: number, clientId?: number): Observable<Blob> {
    let url = `${this.apiUrl}/databaselogs/${database}/export-version/${versionId}`;
    if (clientId) {
      url += `?clientId=${clientId}`;
    }
    return this.http.get(url,
      {
        responseType: 'blob',
        withCredentials: true
      });
  }

  // Label endpoints
  getLabels(database: string): Observable<Label[]> {
    return this.http.get<Label[]>(`${this.apiUrl}/labels/${database}`, { withCredentials: true });
  }

  assignLabel(database: string, logId: number, labelId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/labels/${database}/assign`,
      { logID: logId, labelID: labelId },
      { withCredentials: true });
  }

  removeLabel(database: string, logId: number, labelId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/labels/${database}/assign/${logId}/${labelId}`,
      { withCredentials: true });
  }

  // Client endpoints
  getClients(): Observable<Client[]> {
    return this.http.get<Client[]>(`${this.apiUrl}/clients`, { withCredentials: true });
  }

  getClientLabels(clientId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/clients/${clientId}/labels`, { withCredentials: true });
  }

  getLocalLabels(): Observable<LocalLabel[]> {
    return this.http.get<LocalLabel[]>(`${this.apiUrl}/clients/locallabels`, { withCredentials: true });
  }

  createLocalLabel(labelDesc: string): Observable<LocalLabel> {
    return this.http.post<LocalLabel>(`${this.apiUrl}/clients/locallabels`,
      { labelDesc },
      { withCredentials: true });
  }

  getLabelUsage(labelId: number): Observable<LabelUsage> {
    return this.http.get<LabelUsage>(`${this.apiUrl}/clients/locallabels/${labelId}/usage`, { withCredentials: true });
  }

  deleteLocalLabel(labelId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/clients/locallabels/${labelId}`, { withCredentials: true });
  }

  assignLabelToClient(clientId: number, labelId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/clients/${clientId}/labels/assign`,
      { labelID: labelId },
      { withCredentials: true });
  }

  removeLabelFromClient(clientId: number, labelId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/clients/${clientId}/labels/${labelId}`,
      { withCredentials: true });
  }

  // Client Management endpoints
  getClientsManagement(): Observable<ClientManagement[]> {
    return this.http.get<ClientManagement[]>(`${this.apiUrl}/clientsmanagement`, { withCredentials: true });
  }

  getClientManagement(id: number): Observable<ClientManagement> {
    return this.http.get<ClientManagement>(`${this.apiUrl}/clientsmanagement/${id}`, { withCredentials: true });
  }

  createClient(client: Partial<ClientManagement>): Observable<ClientManagement> {
    return this.http.post<ClientManagement>(`${this.apiUrl}/clientsmanagement`, client, { withCredentials: true });
  }

  updateClient(id: number, client: ClientManagement): Observable<ClientManagement> {
    return this.http.put<ClientManagement>(`${this.apiUrl}/clientsmanagement/${id}`, client, { withCredentials: true });
  }

  deleteClient(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/clientsmanagement/${id}`, { withCredentials: true });
  }

  // Workstation endpoints
  getWorkstations(): Observable<ClientWorkstation[]> {
    return this.http.get<ClientWorkstation[]>(`${this.apiUrl}/workstations`, { withCredentials: true });
  }

  getWorkstationsByClient(clientId: number): Observable<ClientWorkstation[]> {
    return this.http.get<ClientWorkstation[]>(`${this.apiUrl}/workstations/client/${clientId}`, { withCredentials: true });
  }

  getWorkstation(id: number): Observable<ClientWorkstation> {
    return this.http.get<ClientWorkstation>(`${this.apiUrl}/workstations/${id}`, { withCredentials: true });
  }

  createWorkstation(workstation: Partial<ClientWorkstation>): Observable<ClientWorkstation> {
    return this.http.post<ClientWorkstation>(`${this.apiUrl}/workstations`, workstation, { withCredentials: true });
  }

  updateWorkstation(id: number, workstation: ClientWorkstation): Observable<ClientWorkstation> {
    return this.http.put<ClientWorkstation>(`${this.apiUrl}/workstations/${id}`, workstation, { withCredentials: true });
  }

  deleteWorkstation(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/workstations/${id}`, { withCredentials: true });
  }

  assignRoleToWorkstation(workstationId: number, roleTypeId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/workstations/${workstationId}/roles`,
      { roleTypeID: roleTypeId },
      { withCredentials: true });
  }

  removeRoleFromWorkstation(workstationId: number, roleTypeId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/workstations/${workstationId}/roles/${roleTypeId}`,
      { withCredentials: true });
  }

  generateUpgradeToken(workstationId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/workstations/${workstationId}/upgrade-token`, {},
      { withCredentials: true });
  }

  getWorkstationUpgradeLogs(workstationId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/workstations/${workstationId}/upgrade-logs`,
      { withCredentials: true });
  }

  getUntrackedDatabases(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/workstations/untracked-databases`, { withCredentials: true });
  }

  getVersionsForDatabase(database: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/workstations/versions-for-database`,
      { params: { database }, withCredentials: true });
  }

  getOfflinePackageVersions(workstationId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/workstations/${workstationId}/offline-versions`,
      { withCredentials: true });
  }

  downloadOfflinePackage(workstationId: number, targetVersion: string): Observable<Blob> {
    return this.http.post(
      `${this.apiUrl}/workstations/${workstationId}/offline-package?targetVersion=${encodeURIComponent(targetVersion)}`,
      {},
      { withCredentials: true, responseType: 'blob' }
    );
  }

  completeOfflineUpgrade(workstationId: number, body: { achievedVersion: string; machineName?: string; notes?: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/workstations/${workstationId}/complete-offline-upgrade`, body,
      { withCredentials: true });
  }

  // Role Type endpoints
  getRoleTypes(database?: string): Observable<WorkstationRoleType[]> {
    const params: Record<string, string> = {};
    if (database) params['database'] = database;
    return this.http.get<WorkstationRoleType[]>(`${this.apiUrl}/roletypes`, { withCredentials: true, params });
  }

  getRoleType(id: number): Observable<WorkstationRoleType> {
    return this.http.get<WorkstationRoleType>(`${this.apiUrl}/roletypes/${id}`, { withCredentials: true });
  }

  createRoleType(roleType: Partial<WorkstationRoleType>): Observable<WorkstationRoleType> {
    return this.http.post<WorkstationRoleType>(`${this.apiUrl}/roletypes`, roleType, { withCredentials: true });
  }

  updateRoleType(id: number, roleType: WorkstationRoleType): Observable<WorkstationRoleType> {
    return this.http.put<WorkstationRoleType>(`${this.apiUrl}/roletypes/${id}`, roleType, { withCredentials: true });
  }

  deleteRoleType(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/roletypes/${id}`, { withCredentials: true });
  }

  requestRoleTypeArtifact(roleTypeId: number, description?: string): Observable<WorkstationRoleTypeRequest> {
    return this.http.post<WorkstationRoleTypeRequest>(
      `${this.apiUrl}/roletypes/${roleTypeId}/request`,
      { description },
      { withCredentials: true }
    );
  }

  getRoleArtifactStatuses(): Observable<RoleArtifactStatus[]> {
    return this.http.get<RoleArtifactStatus[]>(`${this.apiUrl}/roletypes/artifact-status`, { withCredentials: true });
  }

  uploadRoleArtifact(roleTypeId: number, versionName: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/roletypes/${roleTypeId}/upload`,
      { versionName },
      { withCredentials: true }
    );
  }

  // Database Mapping endpoints
  getDatabaseMappings(): Observable<DatabaseMapping[]> {
    return this.http.get<DatabaseMapping[]>(`${this.apiUrl}/databasemappings`, { withCredentials: true });
  }

  getDatabaseMapping(id: number): Observable<DatabaseMapping> {
    return this.http.get<DatabaseMapping>(`${this.apiUrl}/databasemappings/${id}`, { withCredentials: true });
  }

  getAvailableDatabasesForMapping(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/databasemappings/databases`, { withCredentials: true });
  }

  getSourceDatabasesForMapping(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/databasemappings/databases/source`, { withCredentials: true });
  }

  getTargetDatabasesForMapping(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/databasemappings/databases/target`, { withCredentials: true });
  }

  createDatabaseMapping(mapping: Partial<DatabaseMapping>): Observable<DatabaseMapping> {
    return this.http.post<DatabaseMapping>(`${this.apiUrl}/databasemappings`, mapping, { withCredentials: true });
  }

  updateDatabaseMapping(id: number, mapping: DatabaseMapping): Observable<any> {
    return this.http.put(`${this.apiUrl}/databasemappings/${id}`, mapping, { withCredentials: true });
  }

  deleteDatabaseMapping(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/databasemappings/${id}`, { withCredentials: true });
  }

  getMappingVersionInfo(id: number): Observable<MappingVersionInfo> {
    return this.http.get<MappingVersionInfo>(`${this.apiUrl}/databasemappings/${id}/version-info`, { withCredentials: true });
  }

  upgradeTargetDatabase(id: number): Observable<UpgradeResult> {
    return this.http.post<UpgradeResult>(`${this.apiUrl}/databasemappings/${id}/upgrade`, {}, { withCredentials: true });
  }

  // Version Logs endpoints
  getVersionLogs(database?: string, roleTypeId?: number, pendingOnly?: boolean): Observable<VersionLog[]> {
    const params: Record<string, string> = {};
    if (database) params['database'] = database;
    if (roleTypeId != null) params['roleTypeId'] = roleTypeId.toString();
    if (pendingOnly) params['pendingOnly'] = 'true';
    return this.http.get<{ data: VersionLog[] }>(`${this.apiUrl}/versionlogs`, { withCredentials: true, params })
      .pipe(map(r => r.data));
  }

  getTrackedDatabasesForVersionLogs(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/versionlogs/tracked-databases`, { withCredentials: true });
  }

  retryAwsExport(id: number): Observable<S3ExportResult> {
    return this.http.post<S3ExportResult>(`${this.apiUrl}/versionlogs/${id}/retry`, {}, { withCredentials: true });
  }

  getS3Status(): Observable<S3Status> {
    return this.http.get<S3Status>(`${this.apiUrl}/versionlogs/status`, { withCredentials: true });
  }

  // Presets
  getPresets(): Observable<PresetInfo[]> {
    return this.http.get<PresetInfo[]>(
      `${this.apiUrl}/presets`, { withCredentials: true });
  }

  getPresetDetails(name: string): Observable<PresetJsonStructure> {
    return this.http.get<PresetJsonStructure>(
      `${this.apiUrl}/presets/${encodeURIComponent(name)}`, { withCredentials: true });
  }

  createPreset(request: CreatePresetRequest): Observable<PresetOperationResult> {
    return this.http.post<PresetOperationResult>(
      `${this.apiUrl}/presets`, request, { withCredentials: true });
  }

  regeneratePreset(name: string): Observable<PresetOperationResult> {
    return this.http.post<PresetOperationResult>(
      `${this.apiUrl}/presets/${encodeURIComponent(name)}/regenerate`, {}, { withCredentials: true });
  }

  downloadPreset(name: string): Observable<Blob> {
    return this.http.get(
      `${this.apiUrl}/presets/${encodeURIComponent(name)}/download`,
      { withCredentials: true, responseType: 'blob' });
  }

  deletePreset(name: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/presets/${encodeURIComponent(name)}`, { withCredentials: true });
  }

  // Client Users endpoints (Admin)
  getClientUsers(): Observable<ClientUser[]> {
    return this.http.get<ClientUser[]>(`${this.apiUrl}/clientusers`, { withCredentials: true });
  }

  getClientUsersByClient(clientId: number): Observable<ClientUser[]> {
    return this.http.get<ClientUser[]>(`${this.apiUrl}/clientusers/client/${clientId}`, { withCredentials: true });
  }

  getClientUser(id: number): Observable<ClientUser> {
    return this.http.get<ClientUser>(`${this.apiUrl}/clientusers/${id}`, { withCredentials: true });
  }

  createClientUser(request: CreateClientUserRequest): Observable<ClientUser> {
    return this.http.post<ClientUser>(`${this.apiUrl}/clientusers`, request, { withCredentials: true });
  }

  updateClientUser(id: number, request: UpdateClientUserRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/clientusers/${id}`, request, { withCredentials: true });
  }

  deleteClientUser(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/clientusers/${id}`, { withCredentials: true });
  }

  resendPasswordSetupEmail(id: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/clientusers/${id}/resend-setup-email`, {}, { withCredentials: true });
  }

  getUserLoginHistory(userId: number): Observable<LoginAttempt[]> {
    return this.http.get<LoginAttempt[]>(`${this.apiUrl}/clientusers/${userId}/login-history`, { withCredentials: true });
  }

  getAllLoginAttempts(page: number = 1, pageSize: number = 20, clientId?: number, userId?: number, status?: string): Observable<LoginAttemptsResponse> {
    let params: any = { page, pageSize };
    if (clientId) params.clientId = clientId;
    if (userId) params.userId = userId;
    if (status) params.status = status;
    return this.http.get<LoginAttemptsResponse>(`${this.apiUrl}/clientusers/login-attempts`, { params, withCredentials: true });
  }

  // User Authentication endpoints (User zone)
  userLogin(request: UserLoginRequest): Observable<UserLoginResponse> {
    return this.http.post<UserLoginResponse>(`${this.apiUrl}/userauth/login`, request, { withCredentials: true });
  }

  verifyOtp(request: VerifyOtpRequest): Observable<VerifyOtpResponse> {
    return this.http.post<VerifyOtpResponse>(`${this.apiUrl}/userauth/verify-otp`, request);
  }

  resendOtp(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/userauth/resend-otp`, { email });
  }

  getCurrentAuthenticatedUser(): Observable<AuthenticatedUser> {
    return this.http.get<AuthenticatedUser>(`${this.apiUrl}/userauth/me`, { withCredentials: true });
  }

  userLogout(): Observable<any> {
    return this.http.post(`${this.apiUrl}/userauth/logout`, {}, { withCredentials: true });
  }

  adminLogout(): Observable<any> {
    return this.http.post(`${this.apiUrl}/adminauth/logout`, {}, { withCredentials: true });
  }

  // RAG Chat endpoints (User zone - JWT auth via HttpOnly cookie)
  ragAsk(request: RagAskRequest): Observable<RagAskResponse> {
    return this.http.post<RagAskResponse>(`${this.apiUrl}/rag/ask`, request, { withCredentials: true });
  }

  getRagUsage(): Observable<UsageLimitInfo> {
    return this.http.get<UsageLimitInfo>(`${this.apiUrl}/rag/usage`, { withCredentials: true });
  }

  getRagHistory(page: number = 1, pageSize: number = 20): Observable<ConversationHistoryItem[]> {
    return this.http.get<ConversationHistoryItem[]>(
      `${this.apiUrl}/rag/history?page=${page}&pageSize=${pageSize}`, { withCredentials: true });
  }

  // GCS Document Management endpoints (Admin zone - Windows auth)
  gcsListFiles(path: string = '', useCode?: string): Observable<GcsFileItem[]> {
    let url = `${this.apiUrl}/gcs/list?path=${encodeURIComponent(path)}`;
    if (useCode) url += `&useCode=${encodeURIComponent(useCode)}`;
    return this.http.get<GcsFileItem[]>(url, { withCredentials: true });
  }

  gcsUploadFile(file: File, path: string, useCode?: string): Observable<GcsUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    if (useCode) formData.append('useCode', useCode);
    return this.http.post<GcsUploadResponse>(`${this.apiUrl}/gcs/upload`, formData, { withCredentials: true });
  }

  gcsCreateDirectory(path: string, useCode?: string): Observable<GcsUploadResponse> {
    return this.http.post<GcsUploadResponse>(
      `${this.apiUrl}/gcs/create-directory`,
      { path, useCode },
      { withCredentials: true }
    );
  }

  gcsDownloadFile(path: string, useCode?: string): Observable<Blob> {
    let url = `${this.apiUrl}/gcs/download?path=${encodeURIComponent(path)}`;
    if (useCode) url += `&useCode=${encodeURIComponent(useCode)}`;
    return this.http.get(url, {
      withCredentials: true,
      responseType: 'blob'
    });
  }

  // Reports endpoints (Admin zone - Windows auth)
  getReportTypes(): Observable<ReportType[]> {
    return this.http.get<ReportType[]>(`${this.apiUrl}/reports/types`, { withCredentials: true });
  }

  createReportType(typeName: string, description?: string): Observable<ReportType> {
    return this.http.post<ReportType>(
      `${this.apiUrl}/reports/types`,
      { typeName, description },
      { withCredentials: true }
    );
  }

  getReports(page: number = 1, pageSize: number = 20, typeFilter?: string, moduleFilter?: string): Observable<ReportListResponse> {
    let url = `${this.apiUrl}/reports?page=${page}&pageSize=${pageSize}`;
    if (typeFilter) url += `&typeFilter=${encodeURIComponent(typeFilter)}`;
    if (moduleFilter) url += `&moduleFilter=${encodeURIComponent(moduleFilter)}`;
    return this.http.get<ReportListResponse>(url, { withCredentials: true });
  }

  createReport(request: CreateReportRequest): Observable<Report> {
    return this.http.post<Report>(`${this.apiUrl}/reports`, request, { withCredentials: true });
  }

  getReportModules(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/reports/modules`, { withCredentials: true });
  }

  retryReportUpload(reportId: number): Observable<Report> {
    return this.http.post<Report>(`${this.apiUrl}/reports/${reportId}/retry-upload`, {}, { withCredentials: true });
  }

  // Agent Tasks endpoints (internal instance only)
  getAgentTasks(database?: string, roleId?: number, status?: number, page = 1, pageSize = 20): Observable<AgentTasksResponse> {
    const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
    if (database) params['database'] = database;
    if (roleId != null) params['roleId'] = String(roleId);
    if (status != null) params['status'] = String(status);
    return this.http.get<AgentTasksResponse>(`${this.apiUrl}/agenttasks`, { withCredentials: true, params });
  }

  getAgentTaskLog(taskId: number): Observable<string> {
    return this.http.get(`${this.apiUrl}/agenttasks/${taskId}/log`, {
      withCredentials: true,
      responseType: 'text'
    });
  }

  // ─── Switch 51 endpoints (internal instance only) ─────────────────────────

  isSwitch51Configured(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/switch51/is-configured`, { withCredentials: true });
  }

  saveSwitch51Password(plainText: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/switch51/admin/save-password`,
      JSON.stringify(plainText),
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
    );
  }

  getSwitch51Ports(): Observable<SwitchPortDto[]> {
    return this.http.get<SwitchPortDto[]>(`${this.apiUrl}/switch51/ports`, { withCredentials: true });
  }

  learnSwitch51Port(port: number): Observable<PortActionResult> {
    return this.http.post<PortActionResult>(`${this.apiUrl}/switch51/ports/${port}/learn`, {}, { withCredentials: true });
  }

  blockSwitch51Port(port: number): Observable<PortActionResult> {
    return this.http.post<PortActionResult>(`${this.apiUrl}/switch51/ports/${port}/block`, {}, { withCredentials: true });
  }

  enableSwitch51Port(port: number): Observable<PortActionResult> {
    return this.http.post<PortActionResult>(`${this.apiUrl}/switch51/ports/${port}/enable`, {}, { withCredentials: true });
  }

  // ─── External Admin Auth ───────────────────────────────────────────────────

  adminLogin(request: { email: string; password: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/adminauth/login`, request);
  }

  adminVerifyOtp(request: { email: string; otpCode: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/adminauth/verify-otp`, request);
  }

  adminResendOtp(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/adminauth/resend-otp`, { email });
  }

  adminSetPassword(request: { token: string; password: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/adminauth/set-password`, request);
  }

  adminChangePassword(request: { currentPassword: string; newPassword: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/adminauth/change-password`, request, { withCredentials: true });
  }

  adminGetCurrentUser(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/adminauth/me`, { withCredentials: true });
  }

  // Full page navigation target for "Sign in with Microsoft" — not an HttpClient call.
  adminSsoLoginUrl(): string {
    return `${this.apiUrl}/adminauth/sso/login`;
  }

  // ─── External Admin Management (internal instance only) ───────────────────

  getExternalAdmins(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/externaladmins`, { withCredentials: true });
  }

  createExternalAdmin(request: { email: string; firstName?: string; lastName?: string; password?: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/externaladmins`, request, { withCredentials: true });
  }

  deleteExternalAdmin(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/externaladmins/${id}`, { withCredentials: true });
  }

  deactivateExternalAdmin(id: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/externaladmins/${id}/deactivate`, {}, { withCredentials: true });
  }

  activateExternalAdmin(id: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/externaladmins/${id}/activate`, {}, { withCredentials: true });
  }

  resendExternalAdminSetupEmail(id: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/externaladmins/${id}/resend-setup-email`, {}, { withCredentials: true });
  }
}

